import { Label } from '@/components/ui/label';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import type { WebinarSlide, WebinarSlideNote } from '@/features/webinar/types';
import { webinarService } from '@/services/webinar.service';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { mapHttpError } from '@/lib/mapApiError';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const formNotesSchema = z.object({
  page: z.number(),
  notes: z.string(),
});

export default function PdfNotesForm({
  asset,
  workspaceId,
  webinarId,
}: {
  asset: WebinarSlide;
  workspaceId: number;
  webinarId: number;
}) {
  const { t } = useTranslation('createWebinar');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [notesPerPage, setNotesPerPage] = useState<Record<number, string>>({});
  const [existingNotes, setExistingNotes] = useState<WebinarSlideNote[]>([]);
  const [savingNote, setSavingNote] = useState(false);

  const formNotes = useForm<z.infer<typeof formNotesSchema>>({
    resolver: zodResolver(formNotesSchema),
    defaultValues: {
      page: 1,
      notes: '',
    },
  });

  const handleDocumentLoad = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
  };

  const buildNotesMap = useMemo(
    () => (notes: WebinarSlideNote[]) => {
      const notesMap: Record<number, string> = {};
      notes.forEach((note) => {
        if (note.text) {
          notesMap[note.pageNumber] = note.text;
        }
      });
      return notesMap;
    },
    [],
  );

  useEffect(() => {
    const loadNotes = async () => {
      try {
        const notes = await webinarService.listSlideNotes(workspaceId, webinarId, asset.id);
        setExistingNotes(notes);
        setNotesPerPage(buildNotesMap(notes));
      } catch (error) {
        console.error('Error loading notes:', error);
      }
    };

    loadNotes();
  }, [workspaceId, webinarId, asset.id, buildNotesMap]);

  const handleNoteChange = (page: number, notes: string) => {
    setNotesPerPage((prev) => ({
      ...prev,
      [page]: notes,
    }));
  };

  const handleSaveAllNotes = async () => {
    const hasAnyNote = Object.values(notesPerPage).some((n) => n?.trim());
    if (!hasAnyNote) {
      return;
    }

    setSavingNote(true);
    try {
      const notesToSave =
        totalPages > 0
          ? Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              const text = notesPerPage[pageNumber]?.trim() || null;
              return { pageNumber, text };
            })
          : Object.entries(notesPerPage).map(([page, text]) => ({
              pageNumber: Number(page),
              text: text?.trim() || null,
            }));

      await webinarService.bulkUpsertSlideNotes(workspaceId, webinarId, asset.id, {
        notes: notesToSave,
      });

      const updatedNotes = await webinarService.listSlideNotes(workspaceId, webinarId, asset.id);
      setExistingNotes(updatedNotes);
      setNotesPerPage(buildNotesMap(updatedNotes));
      toast.success(t('pdfNotesForm.saveSuccess'));
    } catch (error) {
      console.error('Error saving notes:', error);
      const err = error as { message?: string } | undefined;
      const errorKey = err?.message ? mapHttpError(err.message) : 'errors.generic';
      toast.error(t(errorKey) || 'Une erreur est survenue');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteAllNotes = async () => {
    const pagesToClear =
      totalPages > 0
        ? Array.from({ length: totalPages }, (_, index) => index + 1)
        : existingNotes.map((note) => note.pageNumber);

    if (pagesToClear.length === 0) {
      return;
    }

    try {
      await webinarService.bulkUpsertSlideNotes(workspaceId, webinarId, asset.id, {
        notes: pagesToClear.map((pageNumber) => ({
          pageNumber,
          text: null,
        })),
      });

      const updatedNotes = await webinarService.listSlideNotes(workspaceId, webinarId, asset.id);
      setExistingNotes(updatedNotes);
      setNotesPerPage({});
      toast.success(t('pdfNotesForm.deleteAllSuccess'));
    } catch (error) {
      console.error('Error deleting all notes', error);
      const err = error as { message?: string } | undefined;
      const errorKey = err?.message ? mapHttpError(err.message) : 'errors.generic';
      toast.error(t(errorKey) || 'Une erreur est survenue');
    }
  };

  useEffect(() => {
    const currentNotes = notesPerPage[currentPage] || '';
    formNotes.setValue('notes', currentNotes);
    formNotes.setValue('page', currentPage);
  }, [currentPage, notesPerPage, formNotes]);

  return (
    <div className="relative  rounded-2xl ">
      <div className=" bg-medium rounded-b-2xl p-6 space-y-4 h-full ">
        {/* top */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label>
              {t('pdfNotesForm.slide')}{' '}
              <span className="text-highlighted">
                {currentPage}/{totalPages}
              </span>
            </Label>
          </div>
          <div
            className="flex items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity"
            onClick={handleDeleteAllNotes}
          >
            <Trash2 className="size-6 text-text-secondary" />
            <Label className="text-text-secondary cursor-pointer">
              {t('pdfNotesForm.deleteAllNotes')}
            </Label>
          </div>
        </div>

        {/* bottom */}
        <div className="flex items-center gap-4 h-[208px] ">
          {/* pdf view */}
          <div className="">
            {/* no cors */}
            <Document
              file={asset.slideUrl}
              onLoadError={(e) => console.error(e)}
              onLoadSuccess={handleDocumentLoad}
              className="rounded-2xl  w-full h-full "
              loading={<Skeleton className="w-[370px] h-[208px] " />}
            >
              <Page
                width={370}
                height={270}
                className="rounded-2xl overflow-hidden"
                pageNumber={currentPage}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={<Skeleton className="w-[370px] h-[208px]" />}
              />
            </Document>
          </div>
          {/* caroussel */}
          <div className="flex shrink-0 flex-col gap-2 max-h-[208px] space-y-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <Document
              file={asset.slideUrl}
              onLoadError={(e) => console.error(e)}
              loading={
                <div className="space-y-4">
                  <Skeleton className="w-14 h-20 " />
                  <Skeleton className="w-14 h-20 " />
                  <Skeleton className="w-14 h-20 " />
                </div>
              }
              className="space-y-4"
            >
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNumber = index + 1;
                const isSelected = pageNumber === currentPage;

                return (
                  <div
                    key={pageNumber}
                    className={`cursor-pointer border-2 rounded-lg transition-all overflow-hidden ${
                      isSelected ? 'border-highlighted' : 'border-transparent'
                    }`}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    <Page
                      width={56}
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={<Skeleton className="w-14 h-[208px]" />}
                    />
                  </div>
                );
              })}
            </Document>
          </div>

          {/* notes */}
          <div className="w-full flex flex-col gap-2 h-full">
            <Textarea
              className="resize-none h-full"
              placeholder={t('pdfNotesForm.notesPlaceholder')}
              rows={8}
              value={notesPerPage[currentPage] || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 1300) {
                  handleNoteChange(currentPage, value);
                }
              }}
              maxLength={1300}
            />
            <div className="flex justify-end">
              <p className="text-xs text-gray-tertiary">
                {notesPerPage[currentPage]?.length || 0}/1300
              </p>
            </div>
            <div className="flex items-center gap-2 w-full">
              <Button
                className="w-full"
                onClick={handleSaveAllNotes}
                disabled={savingNote || !Object.values(notesPerPage).some((n) => n?.trim())}
              >
                {savingNote ? (
                  <span>{t('pdfNotesForm.saving')}</span>
                ) : (
                  <span>{t('pdfNotesForm.save')}</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
